import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Sidebar from "@/components/Sidebar";
import TopBar from "@/components/TopBar";
import AssetCard, { AssetRow } from "@/components/AssetCard";
import AssetDetailModal from "@/components/AssetDetailModal";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { TID } from "@/constants/testIds";
import { Layers, Plus, Trash2, Heart, Edit3, Loader2, Link2, Sparkles, X as XIcon, Pin, PinOff, FolderOpen, ChevronLeft, Check } from "lucide-react";
import ShareModal from "@/components/ShareModal";
import { toast, Toaster } from "sonner";

export default function Collections() {
  const { cid } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = ["admin", "editor"].includes(user?.role);
  const isClient = user?.role === "client";
  // Iter 67 — Clients can create only smart collections. Show the "New
  // collection" button for them too, but pre-enable the smart toggle.
  const canCreate = canEdit || isClient;
  const [folders, setFolders] = useState([]);
  const [collections, setCollections] = useState([]);
  const [view, setView] = useState(() => localStorage.getItem("wd_view") || "grid");
  const [active, setActive] = useState(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [assets, setAssets] = useState([]);
  const [collection, setCollection] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sharing, setSharing] = useState(null); // { id, name } when sharing
  // Iter 65 — Smart Collection creation state.
  // Iter 68 — Logic-First workflow: Phase A picks a folder from the Asset
  // Manager pool, Phase B lists tags actually in use inside that folder,
  // Phase C checkboxes assemble the recipe. Clients skip Phase A entirely.
  const [isSmart, setIsSmart] = useState(false);
  const [smartTags, setSmartTags] = useState([]);
  const [smartFolderId, setSmartFolderId] = useState("");
  const [amFolders, setAmFolders] = useState([]);      // Asset-Manager pool (Phase A)
  const [folderTags, setFolderTags] = useState([]);    // [{name,count}] for chosen folder
  const [tagsLoading, setTagsLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);    // Iter 68 — non-null while editing an existing smart collection
  // Iter 69 — Tag combinator: "any" = at-least-one-tag ($in), "all" = every-tag ($all).
  // Default is "any" for showcase-style buckets like "CLUB Week + Black Friday".
  const [matchMode, setMatchMode] = useState("any");

  useEffect(() => {
    api.get("/folders").then((r) => setFolders(r.data));
    refresh();
    // Iter 68 — Preload Phase A pool for admins/editors only. Clients don't
    // see the folder picker.
    if (!isClient) {
      api.get("/folders/asset-manager").then((r) => setAmFolders(r.data)).catch(() => {});
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!cid) { setCollection(null); setAssets([]); return; }
    api.get(`/collections/${cid}`).then(async ({ data }) => {
      setCollection(data);
      if (data.asset_ids.length === 0) { setAssets([]); return; }
      const all = await api.get("/assets");
      setAssets(all.data.filter((a) => data.asset_ids.includes(a.id)));
    }).catch(() => navigate("/collections"));
  }, [cid, navigate]);

  // Iter 68 — When a folder is picked (Phase A → B transition) OR a client
  // opens the form (auto-locked to their scope root), load the tag universe
  // recursively for that folder. Changing the folder resets the checked set.
  useEffect(() => {
    if (!isSmart) return;
    const fid = isClient ? user?.client_scope_folder_id : smartFolderId;
    if (!fid) { setFolderTags([]); return; }
    setTagsLoading(true);
    api.get(`/folders/${fid}/tags`)
      .then((r) => setFolderTags(r.data))
      .catch(() => setFolderTags([]))
      .finally(() => setTagsLoading(false));
  }, [isSmart, smartFolderId, isClient, user]);

  const refresh = async () => {
    const { data } = await api.get("/collections");
    setCollections(data);
  };

  const resetForm = () => {
    setCreating(false); setName(""); setIsSmart(false);
    setSmartTags([]); setSmartFolderId(""); setFolderTags([]);
    setEditingId(null); setMatchMode("any");
  };

  // Iter 68 — Open the existing collection in the same Phase A/B/C form,
  // preloading folder + checked tags. Choice 5: allow changing the folder,
  // which resets the tag selection (handled inline via "Change folder").
  const startEdit = (c) => {
    setEditingId(c.id);
    setCreating(true);
    setName(c.name);
    setIsSmart(true);
    setSmartFolderId(c.smart_folder_id || "");
    setSmartTags(c.smart_tags || []);
    setMatchMode(c.match_mode || "all"); // Iter 69 — legacy rows default to ALL.
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    if (isSmart && smartTags.length === 0) return;
    if (isSmart && !isClient && !smartFolderId) return; // Phase A required
    setBusy(true);
    try {
      if (editingId) {
        // Iter 68/69 — Edit path: PATCH with new recipe + match_mode.
        const payload = { name: name.trim(), smart_tags: smartTags, match_mode: matchMode };
        if (!isClient) payload.smart_folder_id = smartFolderId;
        try {
          const { data } = await api.patch(`/collections/${editingId}`, payload);
          setCollection(data);
          resetForm();
          await refresh();
          if (data.asset_ids.length > 0) {
            const all = await api.get("/assets");
            setAssets(all.data.filter((a) => data.asset_ids.includes(a.id)));
          } else {
            setAssets([]);
          }
          // Iter 70 — visible confirmation. Previously a successful save
          // closed the modal silently and — if the recipe change happened
          // to keep the same asset list — nothing on-screen moved, so it
          // felt like nothing happened.
          toast.success(
            `Recipe saved — ${(data.match_mode || "all").toUpperCase()} of ${(data.smart_tags || []).map(t => `#${t}`).join(" ")} · ${data.asset_count} ${data.asset_count === 1 ? "asset" : "assets"}`
          );
        } catch (err) {
          toast.error(err.response?.data?.detail || "Could not save recipe");
          throw err;
        }
        return;
      }
      // Iter 65/68/69 — Smart create sends the tag recipe + folder + match_mode.
      const payload = isSmart
        ? { name: name.trim(), is_smart: true, smart_tags: smartTags,
            smart_folder_id: smartFolderId || null, match_mode: matchMode }
        : { name: name.trim(), asset_ids: [] };
      try {
        const { data } = await api.post("/collections", payload);
        resetForm();
        await refresh();
        navigate(`/collections/${data.id}`);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Could not create collection");
        throw err;
      }
    } finally { setBusy(false); }
  };

  const toggleTag = (t) => {
    setSmartTags((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]);
  };

  // Iter 66 — Optimistic pin toggle. Flip UI first, PATCH backend, revert on
  // error. Sidebar reads /collections and filters is_pinned so pinned rows
  // appear/disappear from the "PINNED" section instantly.
  const togglePin = async (c) => {
    const next = !c.is_pinned;
    setCollections((prev) => prev.map((x) => (x.id === c.id ? { ...x, is_pinned: next } : x)));
    try {
      await api.patch(`/collections/${c.id}`, { is_pinned: next });
      // Fire a lightweight event so Sidebar can refresh without prop-drilling.
      window.dispatchEvent(new CustomEvent("wd:collections-updated"));
    } catch (e) {
      // Revert
      setCollections((prev) => prev.map((x) => (x.id === c.id ? { ...x, is_pinned: !next } : x)));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this collection? Videos themselves are NOT deleted.")) return;
    await api.delete(`/collections/${id}`);
    if (cid === id) navigate("/collections");
    refresh();
  };

  // Iter 68 — Resolve the currently-selected folder's display name for the
  // Phase B/C header. For clients we use the scope name shipped in /auth/me.
  const selectedFolderName = isClient
    ? (user?.scope_folder_name || "Your project")
    : (amFolders.find((f) => f.id === smartFolderId)?.name || "");

  return (
    <div className="flex min-h-screen grain-overlay">
      {/* Iter 70 — Mount Sonner locally so Save/Create feedback is visible.
          No global mount exists in App.js; keeping it scoped avoids
          collisions with the ShareView toaster. */}
      <Toaster theme="dark" position="bottom-right" richColors />
      <Sidebar folders={folders} onRefresh={() => api.get("/folders").then((r) => setFolders(r.data))} />
      <div className="flex-1 flex flex-col min-w-0">
        <TopBar view={view} setView={setView} onSearch={() => {}} searchValue="" onUpload={() => {}} canUpload={false} />
        <main className="flex-1 px-4 sm:px-6 md:px-10 lg:px-12 py-6 md:py-8 pb-20">
          <div className="mb-8 flex items-end justify-between gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">
                {collection ? "Collection" : "All"}
              </div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">
                {collection ? collection.name : "Collections"}
              </h1>
              <p className="text-sm text-zinc-500 mt-1">
                {collection
                  ? `${collection.asset_count} ${collection.asset_count === 1 ? "video" : "videos"} curated by ${collection.owner_name}`
                  : `${collections.length} ${collections.length === 1 ? "collection" : "collections"}`}
              </p>
            </div>
            {canCreate && !collection && (
              <button
                data-testid={TID.newCollectionBtn}
                onClick={() => { setCreating(true); if (isClient) setIsSmart(true); }}
                className="shrink-0 flex items-center gap-2 bg-[var(--brand-accent)] text-black font-semibold rounded-full px-4 py-2 text-sm hover:bg-[var(--brand-accent)]/90 transition"
              >
                <Plus className="w-4 h-4" /> New collection
              </button>
            )}
          </div>

          {creating && (
            <form onSubmit={create} data-testid="new-collection-form" className="mb-6 max-w-2xl bg-zinc-950/60 border border-zinc-800/60 rounded-xl p-5 space-y-4">
              <input
                data-testid={TID.newCollectionInput}
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Collection name…"
                className="w-full bg-black/40 border border-zinc-800 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[var(--brand-accent)]/60"
              />
              {/* Iter 65 — Smart Collection toggle. When on, hide the empty
                  "create then curate" flow and show the Phase A → B → C form.
                  Iter 67 — Clients don't see the toggle; they can only make
                  smart collections and it's auto-enabled when they open the form. */}
              {!isClient && (
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    data-testid="smart-collection-toggle"
                    checked={isSmart}
                    onChange={(e) => setIsSmart(e.target.checked)}
                    className="h-4 w-4 accent-[var(--brand-accent)]"
                  />
                  <Sparkles className="w-4 h-4 text-[var(--brand-accent)]" />
                  <span className="text-sm text-zinc-200">Smart Collection</span>
                  <span className="text-xs text-zinc-500">— auto-updates from tags</span>
                </label>
              )}

              {/* Iter 68 — Logic-First workflow.
                   Phase A: Admin/Editor picks a folder from the Asset-Manager
                     pool. Client role skips this and is auto-locked to their
                     scope root (Phase A folder = client_scope_folder_id).
                   Phase B: Once a folder is chosen we render the tags that
                     are actually in use inside it (with counts) as a checklist.
                   Phase C: Checked boxes assemble the "ALL of these tags"
                     recipe that becomes the smart_tags payload. */}
              {isSmart && (
                <div className="space-y-4 pl-4 border-l-2 border-[var(--brand-accent)]/30">
                  {/* Phase A — Folder chooser (admin/editor only) */}
                  {!isClient && !smartFolderId && (
                    <div className="space-y-2" data-testid="smart-phase-a">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[var(--brand-accent)] text-black text-[10px] font-bold">1</span>
                        <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-400">Pick a folder</div>
                      </div>
                      <p className="text-xs text-zinc-500 pl-7 -mt-1">Asset Manager section only — client-shared folders are excluded.</p>
                      {amFolders.length === 0 ? (
                        <div className="pl-7 text-xs text-zinc-500 italic">No Asset Manager folders available.</div>
                      ) : (
                        <div className="pl-7 grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-56 overflow-y-auto pr-1">
                          {amFolders.map((f) => (
                            <button
                              key={f.id}
                              type="button"
                              data-testid={`smart-folder-option-${f.id}`}
                              onClick={() => setSmartFolderId(f.id)}
                              className="group flex items-center gap-2 text-left px-2.5 py-1.5 rounded-md bg-black/30 border border-zinc-800/60 hover:border-[var(--brand-accent)]/50 hover:bg-black/50 transition"
                            >
                              <FolderOpen className="w-3.5 h-3.5 text-zinc-500 group-hover:text-[var(--brand-accent)] shrink-0" />
                              <span className="text-xs text-zinc-200 truncate flex-1">{f.name}</span>
                              <span className="text-[10px] text-zinc-500">{f.asset_count}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Phase B + C — Tag checklist (visible once folder is
                      chosen OR immediately for clients) */}
                  {(isClient || smartFolderId) && (
                    <div className="space-y-2" data-testid="smart-phase-b">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[var(--brand-accent)] text-black text-[10px] font-bold">{isClient ? 1 : 2}</span>
                          <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-400">Choose tags</div>
                          {selectedFolderName && (
                            <>
                              <span className="text-zinc-700">·</span>
                              <div className="flex items-center gap-1 text-[11px] text-[var(--brand-accent)] min-w-0">
                                <FolderOpen className="w-3 h-3 shrink-0" />
                                <span className="truncate" data-testid="smart-selected-folder-name">{selectedFolderName}</span>
                              </div>
                            </>
                          )}
                        </div>
                        {!isClient && smartFolderId && (
                          <button
                            type="button"
                            data-testid="smart-change-folder"
                            onClick={() => { setSmartFolderId(""); setSmartTags([]); setFolderTags([]); }}
                            className="inline-flex items-center gap-1 text-[11px] text-zinc-500 hover:text-[var(--brand-accent)] transition"
                          >
                            <ChevronLeft className="w-3 h-3" /> Change folder
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-zinc-500 pl-7 -mt-1">
                        {matchMode === "any"
                          ? <>Assets need <span className="text-[var(--brand-accent)]">at least one</span> checked tag.</>
                          : <>Assets need <span className="text-[var(--brand-accent)]">every</span> checked tag.</>}
                      </p>
                      {/* Iter 69 — Match-logic segmented control. Sits at the
                          top of Phase B because it changes the meaning of
                          every checkbox below. Default is "any" for
                          multi-project showcase buckets; "all" is the
                          precision-curation mode (Hero AND Approved). */}
                      <div className="pl-7" data-testid="smart-match-mode">
                        <div className="inline-flex rounded-full border border-zinc-800 bg-black/40 p-0.5 text-[11px]">
                          {[
                            { key: "any", label: "ANY" },
                            { key: "all", label: "ALL" },
                          ].map((opt) => {
                            const active = matchMode === opt.key;
                            return (
                              <button
                                key={opt.key}
                                type="button"
                                data-testid={`match-mode-${opt.key}`}
                                data-active={active ? "true" : "false"}
                                onClick={() => setMatchMode(opt.key)}
                                className={`px-3 py-1 rounded-full font-semibold tracking-wider transition ${active ? "bg-[var(--brand-accent)] text-black" : "text-zinc-400 hover:text-zinc-200"}`}
                              >
                                {opt.label}
                              </button>
                            );
                          })}
                        </div>
                        <span className="ml-2 text-[10px] uppercase tracking-[0.15em] text-zinc-500">
                          {matchMode === "any" ? "Match any tag (OR)" : "Match all tags (AND)"}
                        </span>
                      </div>
                      {tagsLoading ? (
                        <div data-testid="smart-tags-loading" className="pl-7 flex items-center gap-2 text-xs text-zinc-500">
                          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading tags…
                        </div>
                      ) : folderTags.length === 0 ? (
                        <div data-testid="smart-tags-empty" className="pl-7 text-xs text-zinc-500 italic">
                          No tags in use in this folder yet — tag some assets first.
                        </div>
                      ) : (
                        <div className="pl-7 flex flex-col gap-0.5 max-h-56 overflow-y-auto pr-1" data-testid="smart-tag-checklist">
                          {folderTags.map((t) => {
                            const checked = smartTags.includes(t.name);
                            return (
                              <label
                                key={t.name}
                                data-testid={`smart-tag-row-${t.name}`}
                                data-checked={checked ? "true" : "false"}
                                className={`group flex items-center gap-2.5 px-2 py-1.5 rounded-md cursor-pointer transition ${checked ? "bg-[var(--brand-accent)]/10" : "hover:bg-white/[0.02]"}`}
                              >
                                <span className={`flex items-center justify-center w-4 h-4 rounded border transition ${checked ? "bg-[var(--brand-accent)] border-[var(--brand-accent)]" : "border-zinc-700 group-hover:border-[var(--brand-accent)]/50"}`}>
                                  {checked && <Check className="w-3 h-3 text-black" strokeWidth={3} />}
                                </span>
                                <input
                                  type="checkbox"
                                  className="sr-only"
                                  checked={checked}
                                  onChange={() => toggleTag(t.name)}
                                />
                                <span className={`text-sm flex-1 truncate ${checked ? "text-zinc-100" : "text-zinc-300"}`}>#{t.name}</span>
                                <span className={`text-[10px] tabular-nums px-1.5 py-0.5 rounded-full ${checked ? "bg-[var(--brand-accent)]/20 text-[var(--brand-accent)]" : "bg-zinc-900 text-zinc-500"}`}>
                                  {t.count}
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                      {smartTags.length > 0 && (
                        <div className="pl-7 pt-1 flex items-center gap-1.5 flex-wrap">
                          <span className="text-[10px] uppercase tracking-[0.15em] text-zinc-500">Recipe:</span>
                          {smartTags.map((t) => (
                            <span key={t} data-testid={`smart-tag-chip-${t}`} className="inline-flex items-center gap-1 text-[11px] bg-[var(--brand-accent)]/10 text-[var(--brand-accent)] border border-[var(--brand-accent)]/40 rounded-full px-2 py-0.5">
                              #{t}
                              <button type="button" onClick={() => toggleTag(t)} className="hover:text-red-400" aria-label={`Remove ${t}`}>
                                <XIcon className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                      {isClient && (
                        <div data-testid="smart-folder-locked" className="pl-7 pt-1 text-[11px] text-[var(--brand-accent)]/80 flex items-center gap-1.5">
                          <Sparkles className="w-3 h-3" />
                          Locked to your project scope
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              <div className="flex items-center gap-2">
                <button data-testid={TID.newCollectionSubmit} type="submit"
                  disabled={busy || (isSmart && smartTags.length === 0) || (isSmart && !isClient && !smartFolderId)}
                  className="bg-[var(--brand-accent)] text-black font-semibold rounded-md px-4 py-2 text-sm hover:bg-[var(--brand-accent)]/90 transition disabled:opacity-60 flex items-center gap-1.5">
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (editingId ? <><Sparkles className="w-3.5 h-3.5" /> Save recipe</> : (isSmart ? <><Sparkles className="w-3.5 h-3.5" /> Create smart</> : "Create"))}
                </button>
                <button type="button" onClick={resetForm} className="text-sm text-zinc-500 hover:text-zinc-300 px-2">Cancel</button>
              </div>
            </form>
          )}

          {/* List of collections OR collection contents */}
          {collection ? (
            <>
              {/* Iter 65 — Recipe pill row: when this collection is smart,
                  show the tags that define it so admins understand why an
                  asset is in the list without opening a detail. */}
              {collection.is_smart && (
                <div data-testid="collection-recipe-row" className="mb-5 flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-[var(--brand-accent)]">
                    <Sparkles className="w-3 h-3" /> Smart recipe
                  </span>
                  <span className="text-zinc-600">·</span>
                  {/* Iter 69 — Header states the tag combinator up front so
                      viewers instantly grok why an asset does or doesn't show
                      up: "ANY of #a #b" vs "ALL of #a #b". */}
                  <span className="text-[11px] text-zinc-500">
                    <span
                      data-testid="recipe-logic-word"
                      className="text-[var(--brand-accent)] font-bold tracking-wider"
                    >
                      {(collection.match_mode || "all").toUpperCase()}
                    </span>{" "}
                    of
                  </span>
                  {(collection.smart_tags || []).map((t) => (
                    <span key={t} data-testid={`recipe-tag-${t}`} className="text-xs bg-[var(--brand-accent)]/10 text-[var(--brand-accent)] border border-[var(--brand-accent)]/40 rounded-full px-2 py-0.5">
                      #{t}
                    </span>
                  ))}
                  {/* Iter 68 — Edit recipe: owners + admins can re-open the
                      Phase A/B/C form with the folder + tags preloaded. */}
                  {(user?.role === "admin" || collection.owner_id === user?.id) && (
                    <button
                      type="button"
                      data-testid="edit-recipe-btn"
                      onClick={() => startEdit(collection)}
                      className="ml-1 inline-flex items-center gap-1 text-[11px] text-zinc-500 hover:text-[var(--brand-accent)] transition"
                    >
                      <Edit3 className="w-3 h-3" /> Edit recipe
                    </button>
                  )}
                </div>
              )}
              {assets.length === 0 ? (
                <div className="py-16 text-center text-zinc-500">
                  <Layers className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                  <div className="text-sm">
                    {collection.is_smart
                      ? "No assets match this recipe yet — tag a video and it'll appear here."
                      : "No videos in this collection yet."}
                  </div>
                  {!collection.is_smart && (
                    <p className="text-xs text-zinc-600 mt-1">Open any video and use &ldquo;Add to collection&rdquo; in the detail panel.</p>
                  )}
                </div>
              ) : view === "grid" ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3">
                  {assets.map((a) => <AssetCard key={a.id} asset={a} onClick={() => setActive(a.id)} />)}
                </div>
              ) : (
                <div className="border-t border-zinc-900/60">
                  {assets.map((a) => <AssetRow key={a.id} asset={a} onClick={() => setActive(a.id)} />)}
                </div>
              )}
            </>
          ) : collections.length === 0 ? (
            <div className="py-20 text-center">
              <div className="w-16 h-16 rounded-full border border-zinc-800 mx-auto flex items-center justify-center mb-4">
                <Layers className="w-7 h-7 text-zinc-600" />
              </div>
              <h3 className="font-heading text-xl font-semibold tracking-tight text-zinc-300">No collections yet</h3>
              <p className="text-sm text-zinc-500 mt-1 max-w-sm mx-auto">Group videos across folders into themed reels — e.g. &ldquo;Best Of 2026&rdquo; or &ldquo;Acme Campaign.&rdquo;</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {collections.map((c) => (
                <Link
                  key={c.id}
                  to={`/collections/${c.id}`}
                  data-testid={TID.collectionItem(c.id)}
                  data-smart={c.is_smart ? "true" : "false"}
                  className="group block p-5 bg-zinc-950/50 border border-zinc-800/60 rounded-xl hover:border-[var(--brand-accent)]/40 transition"
                >
                  <div className="flex items-start justify-between gap-2">
                    {c.is_smart ? (
                      <Sparkles data-testid={`smart-collection-badge-${c.id}`} className="w-5 h-5 text-[var(--brand-accent)] mt-0.5" />
                    ) : (
                      <Layers className="w-5 h-5 text-[var(--brand-accent)] mt-0.5" />
                    )}
                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition">
                      {canEdit && (user?.role === "admin" || c.owner_id === user?.id) && (
                        <button
                          data-testid={`pin-collection-${c.id}`}
                          data-pinned={c.is_pinned ? "true" : "false"}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); togglePin(c); }}
                          title={c.is_pinned ? "Unpin from sidebar" : "Pin to sidebar"}
                          className={c.is_pinned ? "text-[var(--brand-accent)] hover:text-[var(--brand-accent)]/70 transition" : "text-zinc-500 hover:text-[var(--brand-accent)] transition"}
                        >
                          {c.is_pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
                        </button>
                      )}
                      {/* Iter 71 — Share: admin/editor can share any collection
                          with assets; clients can share their OWN smart
                          collections (full ShareModal lets them set password
                          + expiry — matches backend role gate). */}
                      {((canEdit || (isClient && c.owner_id === user?.id && c.is_smart)) && c.asset_count > 0) && (
                        <button
                          data-testid={`share-collection-${c.id}`}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setSharing({ id: c.id, name: c.name }); }}
                          title="Share as portfolio"
                          className="text-zinc-500 hover:text-[var(--brand-accent)] transition"
                        >
                          <Link2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {(user?.role === "admin" || c.owner_id === user?.id) && (
                        <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); remove(c.id); }} className="text-zinc-600 hover:text-red-400 transition">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                  <h3 className="font-heading text-base font-semibold tracking-tight mt-3 truncate">{c.name}</h3>
                  <div className="text-xs text-zinc-500 mt-1">
                    {c.asset_count} {c.asset_count === 1 ? "video" : "videos"} · {c.owner_name}
                    {c.is_smart && <span className="ml-2 text-[10px] uppercase tracking-wider text-[var(--brand-accent)]">Smart</span>}
                    {c.is_pinned && <span data-testid={`pin-badge-${c.id}`} className="ml-2 text-[10px] uppercase tracking-wider text-[var(--brand-accent)] inline-flex items-center gap-0.5"><Pin className="w-2.5 h-2.5" />Pinned</span>}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </main>
      </div>

      {active && (
        <AssetDetailModal
          assetId={active}
          onClose={() => setActive(null)}
          onChange={() => { if (cid) api.get(`/collections/${cid}`).then(({ data }) => setCollection(data)); }}
        />
      )}

      {sharing && (
        <ShareModal
          target={{ kind: "collection", id: sharing.id, title: sharing.name }}
          onClose={() => setSharing(null)}
          onCreated={() => {}}
        />
      )}
    </div>
  );
}
